// screens/ComingSoon.jsx
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTheme } from '../hooks/useTheme';
import {
  Mail,
  Send,
  Sparkles,
  Rocket,
  Zap,
  Shield,
  Users,
  ShoppingBag,
  TrendingUp,
  CheckCircle,
  ArrowRight,
  Globe,
  Star,
  Heart
} from 'lucide-react';

const ComingSoon = () => {
  const { colors } = useTheme();
  const [email, setEmail] = useState('');
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    window.addEventListener('resize', handleResize);

    const handleMouseMove = (e) => {
      setMousePosition({
        x: (e.clientX / window.innerWidth - 0.5) * 20,
        y: (e.clientY / window.innerHeight - 0.5) * 20,
      });
    };
    window.addEventListener('mousemove', handleMouseMove);

    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('mousemove', handleMouseMove);
    };
  }, []);

  const handleSubscribe = (e) => {
    e.preventDefault();
    if (!email) return;
    setIsSubmitting(true);
    // Simulate API call
    setTimeout(() => {
      setIsSubscribed(true);
      setIsSubmitting(false);
      setEmail('');
    }, 1500);
  };

  const features = [
    { icon: <ShoppingBag size={24} />, title: 'Data Packages', description: 'Best data bundles from all networks' },
    { icon: <Zap size={24} />, title: 'Instant Delivery', description: 'Get your data in seconds' },
    { icon: <Shield size={24} />, title: 'Secure Payments', description: '100% safe transactions' },
    { icon: <Users size={24} />, title: 'Agent Network', description: 'Grow your business with us' },
    { icon: <TrendingUp size={24} />, title: 'Best Prices', description: 'Competitive rates always' },
    { icon: <Globe size={24} />, title: 'Nationwide', description: 'Coverage across Ghana' },
  ];

//   const socialLinks = [
//     { icon: <Instagram size={20} />, url: '#', label: 'Instagram' },
//     { icon: <Twitter size={20} />, url: '#', label: 'Twitter' },
//     { icon: <Facebook size={20} />, url: '#', label: 'Facebook' },
//     { icon: <Youtube size={20} />, url: '#', label: 'YouTube' },
//   ];

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #0A0F1E 0%, #1A2540 30%, #0F1B2E 60%, #0A0F1E 100%)',
      position: 'relative',
      overflow: 'hidden',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '20px',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
    }}>
      {/* Animated Background Particles */}
      <div style={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        pointerEvents: 'none',
        overflow: 'hidden',
      }}>
        {[...Array(30)].map((_, i) => (
          <div
            key={i}
            style={{
              position: 'absolute',
              width: `${Math.random() * 4 + 2}px`,
              height: `${Math.random() * 4 + 2}px`,
              borderRadius: '50%',
              background: `rgba(77, 166, 232, ${Math.random() * 0.3 + 0.1})`,
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              animation: `float ${Math.random() * 20 + 10}s linear infinite`,
              animationDelay: `${Math.random() * 10}s`,
              transform: `translate(${mousePosition.x * (i % 3) * 0.1}px, ${mousePosition.y * (i % 3) * 0.1}px)`,
            }}
          />
        ))}
      </div>

      {/* Decorative Glow Orbs */}
      <div style={{
        position: 'absolute',
        top: '-30%',
        right: '-10%',
        width: '60%',
        height: '60%',
        borderRadius: '50%',
        background: 'radial-gradient(circle, rgba(77, 166, 232, 0.15), transparent 70%)',
        pointerEvents: 'none',
        transform: `translate(${mousePosition.x * 0.3}px, ${mousePosition.y * 0.3}px)`,
        transition: 'transform 0.3s ease-out',
      }} />
      <div style={{
        position: 'absolute',
        bottom: '-20%',
        left: '-10%',
        width: '50%',
        height: '50%',
        borderRadius: '50%',
        background: 'radial-gradient(circle, rgba(77, 166, 232, 0.1), transparent 70%)',
        pointerEvents: 'none',
        transform: `translate(${mousePosition.x * -0.2}px, ${mousePosition.y * -0.2}px)`,
        transition: 'transform 0.3s ease-out',
      }} />

      {/* Main Content */}
      <div style={{
        maxWidth: '1100px',
        width: '100%',
        position: 'relative',
        zIndex: 1,
      }}>
        {/* Header */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: isMobile ? '40px' : '60px',
          flexWrap: 'wrap',
          gap: '16px',
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
          }}>
            <div style={{
              width: '48px',
              height: '48px',
              borderRadius: '16px',
              background: 'linear-gradient(135deg, #4DA6E8, #2D7BC8)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 8px 32px rgba(77, 166, 232, 0.3)',
            }}>
              <Rocket size={24} style={{ color: '#fff' }} />
            </div>
            <div>
              <span style={{
                fontSize: isMobile ? '24px' : '28px',
                fontWeight: '800',
                color: '#E4F0F6',
                letterSpacing: '-0.5px',
              }}>
                Azoemart
              </span>
              <span style={{
                display: 'inline-block',
                marginLeft: '10px',
                fontSize: '10px',
                padding: '2px 12px',
                borderRadius: '20px',
                background: 'rgba(77, 166, 232, 0.2)',
                color: '#4DA6E8',
                fontWeight: '600',
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
                verticalAlign: 'middle',
              }}>
                Coming Soon
              </span>
            </div>
          </div>

          {/* <div style={{
            display: 'flex',
            gap: '8px',
            alignItems: 'center',
          }}>
            {socialLinks.map((social, index) => (
              <a
                key={index}
                href={social.url}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  width: '40px',
                  height: '40px',
                  borderRadius: '50%',
                  border: '1px solid rgba(228, 240, 246, 0.15)',
                  background: 'rgba(228, 240, 246, 0.05)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#A8C4D8',
                  transition: 'all 0.3s ease',
                  textDecoration: 'none',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = 'rgba(77, 166, 232, 0.2)';
                  e.currentTarget.style.borderColor = '#4DA6E8';
                  e.currentTarget.style.color = '#4DA6E8';
                  e.currentTarget.style.transform = 'translateY(-2px)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'rgba(228, 240, 246, 0.05)';
                  e.currentTarget.style.borderColor = 'rgba(228, 240, 246, 0.15)';
                  e.currentTarget.style.color = '#A8C4D8';
                  e.currentTarget.style.transform = 'translateY(0)';
                }}
              >
                {social.icon}
              </a>
            ))}
          </div> */}
        </div>

        {/* Hero Section */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
          gap: isMobile ? '40px' : '60px',
          alignItems: 'center',
        }}>
          {/* Left Content */}
          <div>
            <div style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px',
              padding: '6px 16px 6px 12px',
              borderRadius: '50px',
              background: 'rgba(77, 166, 232, 0.12)',
              border: '1px solid rgba(77, 166, 232, 0.2)',
              marginBottom: '24px',
            }}>
              <div style={{
                width: '8px',
                height: '8px',
                borderRadius: '50%',
                background: '#4DA6E8',
                animation: 'pulse 2s infinite',
              }} />
              <span style={{
                fontSize: '13px',
                color: '#4DA6E8',
                fontWeight: '500',
              }}>
                🚀 Launching soon
              </span>
            </div>

            <h1 style={{
              fontSize: isMobile ? '36px' : '56px',
              fontWeight: '800',
              color: '#E4F0F6',
              margin: '0 0 16px 0',
              lineHeight: '1.1',
              letterSpacing: '-1px',
            }}>
              Something{' '}
              <span style={{
                background: 'linear-gradient(135deg, #4DA6E8, #7C5CFC)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
              }}>
                Amazing
              </span>{' '}
              is Coming
            </h1>

            <p style={{
              fontSize: isMobile ? '16px' : '18px',
              color: '#A8C4D8',
              lineHeight: '1.7',
              margin: '0 0 32px 0',
              maxWidth: '500px',
            }}>
              We're building the future of digital services in Ghana. 
              Get ready for the most innovative platform for data, 
              payments, and business growth.
            </p>

            {/* Email Subscription */}
            {!isSubscribed ? (
              <form onSubmit={handleSubscribe} style={{
                display: 'flex',
                gap: '12px',
                maxWidth: '450px',
                flexWrap: 'wrap',
              }}>
                <div style={{
                  flex: 1,
                  position: 'relative',
                  minWidth: '200px',
                }}>
                  <Mail size={18} style={{
                    position: 'absolute',
                    left: '16px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    color: '#5C8AAA',
                  }} />
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Enter your email"
                    required
                    style={{
                      width: '100%',
                      padding: '14px 16px 14px 44px',
                      borderRadius: '12px',
                      border: '2px solid rgba(228, 240, 246, 0.12)',
                      background: 'rgba(228, 240, 246, 0.06)',
                      color: '#E4F0F6',
                      fontSize: '15px',
                      outline: 'none',
                      transition: 'all 0.3s ease',
                      boxSizing: 'border-box',
                    }}
                    onFocus={(e) => {
                      e.target.style.borderColor = '#4DA6E8';
                      e.target.style.boxShadow = '0 0 0 4px rgba(77, 166, 232, 0.15)';
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = 'rgba(228, 240, 246, 0.12)';
                      e.target.style.boxShadow = 'none';
                    }}
                  />
                </div>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  style={{
                    padding: '14px 28px',
                    borderRadius: '12px',
                    border: 'none',
                    background: 'linear-gradient(135deg, #4DA6E8, #3B82F6)',
                    color: '#fff',
                    fontSize: '15px',
                    fontWeight: '600',
                    cursor: isSubmitting ? 'not-allowed' : 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    transition: 'all 0.3s ease',
                    opacity: isSubmitting ? 0.7 : 1,
                    minHeight: '52px',
                    whiteSpace: 'nowrap',
                  }}
                  onMouseEnter={(e) => {
                    if (!isSubmitting) {
                      e.currentTarget.style.transform = 'translateY(-2px)';
                      e.currentTarget.style.boxShadow = '0 8px 32px rgba(77, 166, 232, 0.4)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = 'none';
                  }}
                >
                  {isSubmitting ? (
                    <>
                      <div className="spinner" style={{
                        width: '20px',
                        height: '20px',
                        border: '2px solid rgba(255,255,255,0.3)',
                        borderTop: '2px solid #fff',
                        borderRadius: '50%',
                        animation: 'spin 1s linear infinite',
                      }} />
                      Subscribing...
                    </>
                  ) : (
                    <>
                      Notify Me
                      <Send size={18} />
                    </>
                  )}
                </button>
              </form>
            ) : (
              <div style={{
                padding: '16px 20px',
                borderRadius: '12px',
                background: 'rgba(16, 185, 129, 0.12)',
                border: '1px solid rgba(16, 185, 129, 0.25)',
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                maxWidth: '450px',
              }}>
                <CheckCircle size={24} style={{ color: '#10B981', flexShrink: 0 }} />
                <div>
                  <div style={{ color: '#E4F0F6', fontWeight: '600', fontSize: '15px' }}>
                    You're on the list! 🎉
                  </div>
                  <div style={{ color: '#5C8AAA', fontSize: '13px' }}>
                    We'll notify you when we launch
                  </div>
                </div>
              </div>
            )}

            {/* Trust Badge */}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '16px',
              marginTop: '24px',
              flexWrap: 'wrap',
            }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                color: '#5C8AAA',
                fontSize: '13px',
              }}>
                <Star size={16} style={{ color: '#FCD34D', fill: '#FCD34D' }} />
                <span>100+ users waiting</span>
              </div>
              <div style={{
                width: '1px',
                height: '20px',
                background: 'rgba(228, 240, 246, 0.1)',
              }} />
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                color: '#5C8AAA',
                fontSize: '13px',
              }}>
                <Shield size={16} style={{ color: '#4DA6E8' }} />
                <span>Secure & trusted</span>
              </div>
            </div>
          </div>

          {/* Right Content - Feature Grid */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr 1fr' : '1fr 1fr',
            gap: '12px',
          }}>
            {features.map((feature, index) => (
              <div
                key={index}
                style={{
                  padding: isMobile ? '16px 14px' : '20px 18px',
                  borderRadius: '16px',
                  background: 'rgba(228, 240, 246, 0.04)',
                  border: '1px solid rgba(228, 240, 246, 0.08)',
                  backdropFilter: 'blur(10px)',
                  transition: 'all 0.4s ease',
                  textAlign: 'center',
                  animation: `fadeInUp ${0.5 + index * 0.1}s ease-out`,
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = 'rgba(77, 166, 232, 0.1)';
                  e.currentTarget.style.borderColor = 'rgba(77, 166, 232, 0.3)';
                  e.currentTarget.style.transform = 'translateY(-4px)';
                  e.currentTarget.style.boxShadow = '0 8px 32px rgba(77, 166, 232, 0.15)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'rgba(228, 240, 246, 0.04)';
                  e.currentTarget.style.borderColor = 'rgba(228, 240, 246, 0.08)';
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = 'none';
                }}
              >
                <div style={{
                  width: isMobile ? '40px' : '48px',
                  height: isMobile ? '40px' : '48px',
                  borderRadius: '12px',
                  background: 'linear-gradient(135deg, rgba(77, 166, 232, 0.2), rgba(124, 92, 252, 0.15))',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 10px',
                  color: '#4DA6E8',
                }}>
                  {feature.icon}
                </div>
                <h4 style={{
                  fontSize: isMobile ? '13px' : '15px',
                  fontWeight: '600',
                  color: '#E4F0F6',
                  margin: '0 0 4px 0',
                }}>
                  {feature.title}
                </h4>
                <p style={{
                  fontSize: isMobile ? '11px' : '12px',
                  color: '#5C8AAA',
                  margin: 0,
                  lineHeight: '1.4',
                }}>
                  {feature.description}
                </p>
              </div>
            ))}
          </div>
        </div>

        {/* Footer */}
        <div style={{
          marginTop: isMobile ? '48px' : '64px',
          paddingTop: '24px',
          borderTop: '1px solid rgba(228, 240, 246, 0.06)',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: '16px',
        }}>
          <div style={{
            fontSize: '13px',
            color: '#5C8AAA',
          }}>
            © {new Date().getFullYear()} Azoemart. All rights reserved.
          </div>
          <div style={{
            display: 'flex',
            gap: '20px',
            flexWrap: 'wrap',
          }}>
            <Link to="#" style={{
              fontSize: '13px',
              color: '#5C8AAA',
              textDecoration: 'none',
              transition: 'color 0.3s ease',
            }}
            onMouseEnter={(e) => e.target.style.color = '#4DA6E8'}
            onMouseLeave={(e) => e.target.style.color = '#5C8AAA'}
            >
              Privacy Policy
            </Link>
            <Link to="#" style={{
              fontSize: '13px',
              color: '#5C8AAA',
              textDecoration: 'none',
              transition: 'color 0.3s ease',
            }}
            onMouseEnter={(e) => e.target.style.color = '#4DA6E8'}
            onMouseLeave={(e) => e.target.style.color = '#5C8AAA'}
            >
              Terms of Service
            </Link>
            <Link to="#" style={{
              fontSize: '13px',
              color: '#5C8AAA',
              textDecoration: 'none',
              transition: 'color 0.3s ease',
            }}
            onMouseEnter={(e) => e.target.style.color = '#4DA6E8'}
            onMouseLeave={(e) => e.target.style.color = '#5C8AAA'}
            >
              Contact
            </Link>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        @keyframes pulse {
          0%, 100% { opacity: 1; transform: scale(1); }
          50% { opacity: 0.5; transform: scale(1.2); }
        }
        @keyframes float {
          0% { transform: translateY(0px) translateX(0px); }
          50% { transform: translateY(-30px) translateX(10px); }
          100% { transform: translateY(0px) translateX(0px); }
        }
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        @media (max-width: 480px) {
          .feature-grid {
            grid-template-columns: 1fr 1fr !important;
          }
        }
        
        * {
          box-sizing: border-box;
        }
      `}</style>
    </div>
  );
};

export default ComingSoon;